import { IconCirclePlus } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SelectOption } from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import {
	showErrorNotification,
	showSuccessNotification,
} from "molecules/notifications/notificationEvents";
import { LoadingState } from "molecules/loadingState/LoadingState";
import { MissingDataState } from "molecules/missingDataState/MissingDataState";
import { ConfirmDialog } from "molecules/dialog/ConfirmDialog";
import { useGetLegalEntitiesQuery } from "src/common/service/nexus/nexusApi.js";
import {
	Card,
	CardHeader,
	CardItemsHeader,
} from "src/features/planning/components/Card.js";
import { EmptyState } from "src/features/planning/components/EmptyState.js";
import { useLoadedCompanyDomain } from "src/routes/routeDataHooks.js";
import {
	useGetApiKeysQuery,
	useSetApiKeyActiveMutation,
} from "./api/apiKeyApi";
import type { ApiKey } from "./apiKeyTypes";
import { ApiKeysTable } from "./ApiKeysTable";
import { CreateApiKeyDialog } from "./CreateApiKeyDialog";

export const ApiKeysSettingsPage = () => {
	const { t } = useTranslation();
	const companyDomain = useLoadedCompanyDomain();
	const companyDomainId = companyDomain.id;

	const {
		data: apiKeys,
		isLoading,
		isError,
		refetch,
	} = useGetApiKeysQuery({ companyDomainId });
	const { data: legalEntities = [] } =
		useGetLegalEntitiesQuery(companyDomainId);
	const [setApiKeyActive, { isLoading: isTogglingActive }] =
		useSetApiKeyActiveMutation();

	const [createOpen, setCreateOpen] = useState(false);
	const [deactivatingKey, setDeactivatingKey] = useState<ApiKey | null>(null);

	const legalEntityByMasterKey = useMemo(
		() =>
			new Map(
				legalEntities.map((le) => [
					le.legalEntityMasterKey,
					{ id: le.id, name: le.name },
				]),
			),
		[legalEntities],
	);

	const legalEntityOptions = useMemo<SelectOption[]>(
		() =>
			legalEntities.map((le) => ({
				label: le.name,
				value: le.legalEntityMasterKey,
			})),
		[legalEntities],
	);

	async function updateActive(apiKey: ApiKey, active: boolean) {
		try {
			await setApiKeyActive({
				companyDomainId,
				apiKeyId: apiKey.apiKey,
				active,
			}).unwrap();
			showSuccessNotification({
				message: active ? t("API key activated") : t("API key deactivated"),
			});
			return true;
		} catch {
			showErrorNotification({ message: t("Failed to update API key") });
			return false;
		}
	}

	function handleToggleActive(apiKey: ApiKey, nextActive: boolean) {
		if (nextActive) {
			void updateActive(apiKey, true);
		} else {
			setDeactivatingKey(apiKey);
		}
	}

	async function handleConfirmDeactivate() {
		if (!deactivatingKey) return;
		const success = await updateActive(deactivatingKey, false);
		if (success) {
			setDeactivatingKey(null);
		}
	}

	const createButton = (
		<Button icon={<IconCirclePlus />} onClick={() => setCreateOpen(true)}>
			{t("Create API key")}
		</Button>
	);

	return (
		<Card>
			<CardHeader>
				<CardItemsHeader heading={t("API keys")} count={apiKeys?.length ?? 0} />
				{createButton}
			</CardHeader>
			{isLoading ? (
				<LoadingState />
			) : isError ? (
				<div className="flex flex-col items-center gap-4 pb-12">
					<MissingDataState title={t("Could not load API keys.")} />
					<Button variant="secondaryGray" onClick={() => refetch()}>
						{t("Try again")}
					</Button>
				</div>
			) : !apiKeys || apiKeys.length === 0 ? (
				<EmptyState
					header={t("No API keys found")}
					createButton={createButton}
				/>
			) : (
				<div className="overflow-x-auto">
					<ApiKeysTable
						apiKeys={apiKeys}
						companyDomainId={companyDomainId}
						legalEntityByMasterKey={legalEntityByMasterKey}
						onToggleActive={handleToggleActive}
						isToggling={isTogglingActive}
					/>
				</div>
			)}
			<CreateApiKeyDialog
				open={createOpen}
				onClose={() => setCreateOpen(false)}
				companyDomainId={companyDomainId}
				legalEntityOptions={legalEntityOptions}
			/>
			<ConfirmDialog
				destructive
				open={deactivatingKey !== null}
				isLoading={isTogglingActive}
				onClose={() => setDeactivatingKey(null)}
				onConfirm={handleConfirmDeactivate}
				title={t("Deactivate API key")}
				description={t(
					"Are you sure you want to deactivate this API key? Integrations using it will stop working.",
				)}
				confirmButtonLabel={t("Deactivate")}
			/>
		</Card>
	);
};
